import React, { useEffect } from 'react';
import getInstallationTotalExpense from 'services/products/calculations/getInstallationTotalExpense';
import { getSellingPriceQuantityFactor } from 'services/products/calculations/getSellingPriceForMargin';
import numericStringToFloat from 'services/numbers/numericStringToFloat';
import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import { Col, Row } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import apiInstallationPrices from 'api/installationPrices';
import apiProducts from 'api/products';
import IProduct from 'api/interfaces/products/IProductGet';
import formatFloatForDisplay from 'services/numbers/formatFloatForDisplay';
import getShownDifference from 'services/numbers/getShownDifference';
import getContributionMarginRatio from 'services/products/calculations/getContributionMarginRatio';
import getSellingPricePerUnit from 'services/products/calculations/getSellingPricePerUnit';
import getExpensePerUnit from 'services/products/calculations/getExpensePerUnit';
import getExpenseTotal from 'services/products/calculations/getExpenseTotal';
import getFormStateHandler from 'services/forms/getFormStateHandler';
import getSellingPriceTotal from 'services/products/calculations/getSellingPriceTotal';
import NumberWithDropdownInputField from 'components/layout/forms/NumberWithDropdownInputField';
import NumberWithUnitInputField from 'components/layout/forms/NumberWithUnitInputField';
import SearchDropdown from 'components/layout/forms/SearchDropdown';
import SelectInputFieldControlled from 'components/layout/forms/SelectInputFieldControlled';
import TextInputFieldNoLabel from 'components/layout/forms/TextInputFieldNoLabel';
import translations from 'services/translations/translations';
import TrashIcon from 'components/layout/icons/TrashIcon';
import { fetchUnits } from 'store/products/actions';

import {
  MAX_QUANTITY_DIGITS,
  MAX_SOCIAL_SECURITY_COST_PERCENTAGE_DIGITS,
} from 'services/offers/inputLimits';
import { useCurrency } from 'services/offers/currency';
import {
  DAILY_ALLOWANCE_PERCENTAGE,
  DAILY_ALLOWANCE_PRICE,
  DAILY_ALLOWANCE_TEXT,
  DAILY_ALLOWANCE_UNIT,
  getDailyAllowanceQuantity,
} from 'services/offers/dailyAllowance';
import { DAILY_ALLOWANCE } from 'services/enums/offers/installationTypes';
import { useCategoryChoices } from 'services/offers/offerChoices';
import {
  withQuantityKeepingPrice,
  withSellingPriceAsSum,
} from '../givenSellingPrice';
import { priceForTargetMargin, withTargetMargin } from '../targetMargin';
import getMarginRatioClassName from '../getMarginRatioClassName';
import PositionUnitPrice from '../PositionUnitPrice';
import useMarginInputs from '../useMarginInputs';
import getInstallationCategoryOptions from '../../../../services/getInstallationCategoryOptions';
import getSellingPriceUnitOptions, {
  getSumUnitOptions,
} from '../../../../services/getSellingPriceUnitOptions';
import getUnitOptions from '../../../../services/getUnitOptions';
import IInstallation from '../../IInstallation';
import { OTHER_EXPENSE } from '../../../../services/installationTypes';

interface ILabelInstallationPrice {
  category: string;
  installationPrice: number;
  installationPriceUnit: string;
  // A product of the product register on a row of the other expenses
  isProduct?: boolean;
  label: string;
  value: string;
}

// The products of the register found for a row of the other expenses
const PRODUCTS_FOUND = 30;

const Installation = ({
  locked,
  installation,
  quantity,
  removeInstallation,
  setInstallation,
  targetMarginPercent,
  type,
  workerDays = 0,
}: {
  locked: boolean;
  installation: IInstallation;
  quantity: number;
  removeInstallation: (installation: IInstallation) => void;
  setInstallation: (installation: IInstallation) => void;
  // The target margin % of the window for the rows following it
  targetMarginPercent: string;
  type: string;
  // The worker days of the installation contract (work days for each installer), the
  // quantity of a per diem row
  workerDays?: number;
}): JSX.Element => {
  // A new row prices itself for its target margin until its price is set
  const setPricedInstallation = (updated: IInstallation) =>
    setInstallation(
      withTargetMargin(
        installation,
        updated,
        getInstallationTotalExpense,
        targetMarginPercent
      )
    );

  // The price is reset (not set by the user): a row following the target margin keeps
  // following it and is priced again
  const setInstallationWithResetPrice = (updated: IInstallation) =>
    setInstallation(
      priceForTargetMargin(
        updated,
        getInstallationTotalExpense,
        targetMarginPercent
      )
    );

  const { t } = useTranslation([translations.products]);
  const categoryChoices = useCategoryChoices();
  const currency = useCurrency();

  const offerIsLocked = () => locked;

  const dispatch = useDispatch();
  const units = useSelector((state: any) => state.products.units);
  // + makes the row a free text row (and back to the search), as on the material rows;
  // the name of the chosen installation is the text to start from, the prices are kept
  const toggleCustom = () =>
    setInstallation({
      ...installation,
      customInstallation:
        !installation.isCustomInstallation && installation.installation?.label
          ? installation.installation.label
          : installation.customInstallation,
      installation: null,
      isCustomInstallation: !installation.isCustomInstallation,
    });

  const handleInstallationPriceChange = (
    installationPrice: ILabelInstallationPrice | null
  ) => {
    if (installationPrice === null) {
      setInstallationWithResetPrice({
        ...installation,
        installation: null,
        sellingPrice: '',
        sellingPriceUnit: '',
      });

      return;
    }

    // A product of the register is not an installation price: the row gets its name as
    // its text, with its price and unit
    if (installationPrice.isProduct) {
      setPricedInstallation({
        ...installation,
        customInstallation: installationPrice.label,
        installation: null,
        installationPrice: formatFloatForDisplay(
          installationPrice.installationPrice
        ),
        installationPriceUnit:
          installationPrice.installationPriceUnit ||
          installation.installationPriceUnit,
        isCustomInstallation: true,
        quantityUnit:
          installationPrice.installationPriceUnit || installation.quantityUnit,
      });
      return;
    }

    setPricedInstallation({
      ...installation,
      category: installationPrice.category,
      installation: {
        label: installationPrice.label,
        value: installationPrice.value,
      },
      installationPrice: formatFloatForDisplay(
        installationPrice.installationPrice
      ),
      installationPriceUnit: installationPrice.installationPriceUnit,
      quantityUnit: installationPrice.installationPriceUnit,
    });
  };

  const installationCategories = useSelector(
    (state: any) => state.products.installationCategories
  );

  useEffect(() => {
    dispatch(fetchUnits());
  }, []);

  useEffect(() => {
    const installationPriceUnit =
      installation.installationPriceUnit === '' ||
      installation.installationPriceUnit === installation.quantityUnit
        ? installation.installationPriceUnit
        : installation.quantityUnit;

    const sellingPriceUnit =
      installation.sellingPriceUnit === '' ||
      installation.sellingPriceUnit === installation.quantityUnit
        ? installation.sellingPriceUnit
        : installation.quantityUnit;

    const changed =
      installation.installationPriceUnit !== installationPriceUnit ||
      installation.sellingPriceUnit !== sellingPriceUnit;

    if (!changed) {
      return;
    }

    setPricedInstallation({
      ...installation,
      installationPriceUnit,
      sellingPriceUnit,
    });
  }, [installation]);

  const loadInstallationPrices = async (
    inputValue: string
  ): Promise<
    | ILabelInstallationPrice[]
    | { label: string; options: ILabelInstallationPrice[] }[]
  > => {
    const list = await apiInstallationPrices.get({
      limit: 100,
      offset: 0,
      search: inputValue,
      type,
    });

    const prices = list.map((installationPrice) => ({
      category: installationPrice.category,
      installationPrice: installationPrice.sellingPrice,
      installationPriceUnit: installationPrice.sellingPriceUnit,
      label: installationPrice.name,
      value: installationPrice.id.toString(),
    }));

    if (type !== OTHER_EXPENSE) {
      return prices;
    }

    // The other expenses can also be products of the register, with the price ticked for
    // the offers (or their first price)
    const products = (
      (await apiProducts
        .get(PRODUCTS_FOUND, 0, inputValue, undefined, false)
        .catch(() => [])) as IProduct[]
    ).map((product) => {
      const price =
        product.prices?.find((productPrice) => productPrice.useInOffers) ??
        product.prices?.[0];
      return {
        category: installation.category,
        installationPrice: Number(price?.value ?? 0),
        installationPriceUnit: price?.siUnit ?? '',
        isProduct: true,
        label: [null, ''].includes(product.displayName)
          ? product.name ?? ''
          : product.displayName,
        value: `product-${product.id}`,
      };
    });

    return [
      {
        label: t(`${translations.calculationWindow}:otherExpenses.Prices`),
        options: prices,
      },
      {
        label: t(`${translations.calculationWindow}:otherExpenses.Products`),
        options: products,
      },
    ];
  };

  const handleOtherFields = getFormStateHandler(
    installation,
    setPricedInstallation
  );

  // A given selling price is the price for one unit of the position, as on the material
  // rows (see givenSellingPrice): a typed price is a sum, and a price per unit keeps its
  // price for a unit of the position when the quantity changes
  const handleFormState = (event: any) => {
    // A per diem row is a row of its own: its text, unit and expense are preset and its
    // quantity comes from the work days
    if (
      event.target.name === 'category' &&
      event.target.value === DAILY_ALLOWANCE
    ) {
      setPricedInstallation({
        ...installation,
        category: DAILY_ALLOWANCE,
        customInstallation: DAILY_ALLOWANCE_TEXT,
        installation: null,
        installationPrice: DAILY_ALLOWANCE_PRICE,
        installationPriceUnit: DAILY_ALLOWANCE_UNIT,
        isCustomInstallation: true,
        quantity: getDailyAllowanceQuantity(workerDays, quantity),
        quantityUnit: DAILY_ALLOWANCE_UNIT,
        socialSecurityCostPercentage: DAILY_ALLOWANCE_PERCENTAGE,
      });
      return;
    }

    if (event.target.name === 'sellingPrice') {
      setPricedInstallation({
        ...installation,
        sellingPrice: event.target.value,
        sellingPriceUnit: '',
      });
      return;
    }

    if (event.target.name === 'quantity') {
      setPricedInstallation(
        withQuantityKeepingPrice(installation, event.target.value)
      );
      return;
    }

    handleOtherFields(event);
  };

  const sellingPriceUnitOptions = getSellingPriceUnitOptions(
    installation.quantityUnit,
    installation.key.toString(),
    t,
    currency
  );

  const contributionMarginRatio = getContributionMarginRatio(
    [installation],
    []
  );

  const expenseForUnit = getExpensePerUnit([installation], []);
  const expenseTotal = getExpenseTotal([installation], [], quantity);

  // The margins shown: the shown selling price minus the shown expense, so they add up
  const contributionMarginForUnit = getShownDifference(
    getSellingPricePerUnit([installation], []),
    expenseForUnit
  );
  const contributionMarginTotal = getShownDifference(
    getSellingPriceTotal([installation], [], quantity),
    expenseTotal
  );

  const marginRatioClassName = getMarginRatioClassName(
    contributionMarginRatio,
    installation.sellingPrice
  );

  const marginInputs = useMarginInputs({
    contributionMargin: contributionMarginForUnit,
    contributionMarginRatio,
    expense: expenseForUnit,
    quantityFactor: getSellingPriceQuantityFactor(
      installation.sellingPriceUnit,
      numericStringToFloat(installation.quantity)
    ),
    // A price set through a margin is given too: the sum it makes for a unit of the position
    setSellingPrice: (sellingPrice: string) =>
      setPricedInstallation(
        withSellingPriceAsSum({ ...installation, sellingPrice })
      ),
  });
  const sellingPriceTotal = getSellingPriceTotal([installation], [], quantity);

  const SMALL_COLUMN_CLASS = 'small-column';

  // The category of an other expense can always be chosen, e.g. a per diem
  const categoryDisabled =
    offerIsLocked() ||
    (type !== OTHER_EXPENSE && !installation.isCustomInstallation);

  const isDailyAllowance = installation.category === DAILY_ALLOWANCE;

  return (
    <>
      <Row className="installation-row">
        <Col>
          <SelectInputFieldControlled
            disabled={categoryDisabled}
            id={`product-installation-${installation.key}-category`}
            name="category"
            onChange={handleFormState}
            options={getInstallationCategoryOptions(
              installationCategories,
              t,
              type,
              { ...categoryChoices, current: installation.category }
            )}
            required
            value={installation.category}
          />
        </Col>
        <Col>
          {installation.isCustomInstallation ? (
            <TextInputFieldNoLabel
              id={`product-installation-${installation.key}-installation`}
              name="customInstallation"
              onChange={handleFormState}
              value={
                installation.customInstallation === null
                  ? ''
                  : installation.customInstallation
              }
              disabled={offerIsLocked()}
            />
          ) : (
            <SearchDropdown
              className="text-field search-field"
              disabled={offerIsLocked()}
              id={`product-installation-${installation.key}-installation`}
              loadOptions={loadInstallationPrices}
              onChange={handleInstallationPriceChange}
              name="installation"
              placeholder={t(`${translations.common}:Search`)}
              required
              value={installation.installation}
            />
          )}
        </Col>
        <Col className="icon-column material-actions">
          <button
            aria-label={t(`${translations.calculationWindow}:Free text row`)}
            aria-pressed={installation.isCustomInstallation}
            className={`material-action ${
              installation.isCustomInstallation ? 'active' : ''
            }`}
            disabled={offerIsLocked()}
            onClick={toggleCustom}
            title={t(`${translations.calculationWindow}:Free text row`)}
            type="button"
          >
            +
          </button>
        </Col>
        <Col>
          <NumberWithDropdownInputField
            dropdownDisabled={!installation.isCustomInstallation}
            dropdownName="quantityUnit"
            dropdownOnChange={handleFormState}
            dropdownOptions={getUnitOptions(units, t)}
            dropdownValue={installation.quantityUnit}
            id={`product-installation-${installation.key}-quantity`}
            maxIntegerDigits={MAX_QUANTITY_DIGITS}
            numberName="quantity"
            numberOnChange={handleFormState}
            numberValue={installation.quantity}
            required
            // The quantity of a per diem follows the work days
            disabled={offerIsLocked() || isDailyAllowance}
          />
        </Col>
        <Col>
          <NumberWithDropdownInputField
            dropdownDisabled
            dropdownName="installationPriceUnit"
            dropdownOnChange={handleFormState}
            dropdownOptions={sellingPriceUnitOptions}
            dropdownValue={installation.installationPriceUnit}
            id={`product-installation-${installation.key}-installation-price`}
            numberName="installationPrice"
            numberOnChange={handleFormState}
            numberValue={installation.installationPrice}
            required
            disabled={offerIsLocked()}
          />
        </Col>
        <Col className={SMALL_COLUMN_CLASS}>
          <NumberWithUnitInputField
            id={`product-installation-${installation.key}-social-security-cost-percentage`}
            onChange={handleFormState}
            maxIntegerDigits={MAX_SOCIAL_SECURITY_COST_PERCENTAGE_DIGITS}
            name="socialSecurityCostPercentage"
            required
            unit="%"
            value={installation.socialSecurityCostPercentage}
            disabled={offerIsLocked()}
          />
        </Col>
        <Col className={SMALL_COLUMN_CLASS}>
          <NumberWithUnitInputField
            disabled
            unit={currency}
            value={formatGroupedNumber(expenseForUnit)}
          />
        </Col>
        <Col>
          <NumberWithDropdownInputField
            dropdownDisabled
            dropdownName="sellingPriceUnit"
            dropdownOnChange={handleFormState}
            dropdownOptions={getSumUnitOptions(
              installation.key.toString(),
              currency
            )}
            dropdownValue=""
            id={`product-installation-${installation.key}-selling-price`}
            displayDecimals={2}
            numberName="sellingPrice"
            numberOnChange={handleFormState}
            // A price per unit of the row is shown as the sum it makes, as a typed price
            numberValue={withSellingPriceAsSum(installation).sellingPrice}
            required
            disabled={offerIsLocked()}
          />
        </Col>
        {/* The selling price of the row for one unit of the position */}
        <Col className={SMALL_COLUMN_CLASS}>
          <PositionUnitPrice
            price={getSellingPricePerUnit([installation], [])}
          />
        </Col>
        <Col className={SMALL_COLUMN_CLASS}>
          <NumberWithUnitInputField
            disabled={offerIsLocked()}
            onBlur={marginInputs.eur.onBlur}
            onChange={marginInputs.eur.onChange}
            unit={currency}
            value={marginInputs.eur.value}
          />
        </Col>
        <Col className={SMALL_COLUMN_CLASS}>
          <NumberWithUnitInputField
            disabled
            unit={currency}
            value={formatGroupedNumber(expenseTotal)}
          />
        </Col>
        <Col className={SMALL_COLUMN_CLASS}>
          <NumberWithUnitInputField
            disabled
            unit={currency}
            value={formatGroupedNumber(sellingPriceTotal)}
          />
        </Col>
        <Col className={SMALL_COLUMN_CLASS}>
          <NumberWithUnitInputField
            disabled
            unit={currency}
            value={formatGroupedNumber(contributionMarginTotal)}
          />
        </Col>
        <Col className={`${SMALL_COLUMN_CLASS} ${marginRatioClassName}`}>
          <NumberWithUnitInputField
            disabled={offerIsLocked()}
            onBlur={marginInputs.percent.onBlur}
            onChange={marginInputs.percent.onChange}
            unit="%"
            value={marginInputs.percent.value}
          />
        </Col>
        <Col className="icon-column">
          {offerIsLocked() ? null : (
            <TrashIcon onClick={() => removeInstallation(installation)} />
          )}
        </Col>
      </Row>
    </>
  );
};

export default Installation;
